import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { NativeSelect } from "@/components/ui/native-select";
import ChipInput from "@/components/ui/chip-input";
import { useCurrency } from "@/context/CurrencyContext";
import { sizePresetsFor, COLOR_PRESETS } from "@/data/variantPresets";
import { RATE_BASIS_OPTIONS, rateBasisSuffix } from "@/data/rateBasis";
import { Label } from "@/components/ui/label";
import { Loader2, Sparkles, Upload, X, MapPin, Crown, Navigation } from "lucide-react";
import { toast } from "sonner";
import LocationPicker from "@/components/listing/LocationPicker";

const CreateListing = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { supported } = useCurrency();
  // Country → suggested local currency. Handles full names, short aliases
  // (UK/USA) and ISO-2 codes (NG/GB) since user.country format varies.
  const countryDefaults = React.useMemo(() => ({
    // Full names
    "Nigeria": "NGN", "Ghana": "GHS", "Kenya": "KES", "South Africa": "ZAR",
    "Zimbabwe": "USD", "Cameroon": "XAF", "Uganda": "UGX", "Tanzania": "TZS",
    "Ethiopia": "ETB", "Senegal": "XAF", "Côte d'Ivoire": "XAF", "Egypt": "EGP",
    "Morocco": "USD", "United Kingdom": "GBP", "United States": "USD", "Canada": "CAD",
    // Short aliases
    "UK": "GBP", "USA": "USD", "US": "USD",
    // ISO-2 codes (older accounts stored these)
    "NG": "NGN", "GH": "GHS", "KE": "KES", "ZA": "ZAR", "ZW": "USD",
    "CM": "XAF", "UG": "UGX", "TZ": "TZS", "ET": "ETB", "SN": "XAF",
    "CI": "XAF", "EG": "EGP", "MA": "USD", "GB": "GBP", "CA": "CAD",
  }), []);
  const resolveCcy = (c) => {
    if (!c) return "USD";
    return countryDefaults[c] || countryDefaults[Object.keys(countryDefaults).find(k => k.toLowerCase() === c.toLowerCase()) || ""] || "USD";
  };
  const fileRef = useRef(null);
  const [meta, setMeta] = useState({ countries: [], product_categories: [], service_categories: [] });
  const [aiBusy, setAiBusy] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiDrafting, setAiDrafting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isCustomCity, setIsCustomCity] = useState(false);
  const [geoLoading, setGeoLoading] = useState(false);

  const initialCountry = user?.country || "Nigeria";
  const [form, setForm] = useState({
    type: "product",
    title: "",
    description: "",
    category: "",
    price: "",
    currency: resolveCcy(initialCountry),
    country: initialCountry,
    city: "",
    images: [],
    lat: null,
    lng: null,
    sizes: [],
    colors: [],
    condition: "new",
    rate_basis: "hour",
  });

  useEffect(() => { api.get("/meta").then((r) => setMeta(r.data)); }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const runAiDraft = async () => {
    if (!aiPrompt.trim()) { toast.error("Type what you're selling first"); return; }
    setAiDrafting(true);
    try {
      const { data } = await api.post("/ai/create-listing-draft", { prompt: aiPrompt.trim(), type: form.type });
      setForm((f) => ({
        ...f,
        title: data.title || f.title,
        category: data.category || f.category,
        description: data.description || f.description,
        price: data.suggested_price ? String(data.suggested_price) : f.price,
      }));
      toast.success("AI filled in your listing — review and edit before posting");
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI couldn't draft that, please try again");
    } finally {
      setAiDrafting(false);
    }
  };

  // Use device geolocation → reverse-geocode via OpenStreetMap Nominatim
  // (free, no key) → pre-fill country + city + lat/lng on the form. Buyer
  // /seller convenience so they don't have to scroll through dropdowns.
  const useMyLocation = async () => {
    if (!navigator.geolocation) {
      toast.error("Your browser doesn't support location lookup");
      return;
    }
    setGeoLoading(true);
    try {
      const pos = await new Promise((resolve, reject) =>
        navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 12000, enableHighAccuracy: false })
      );
      const { latitude, longitude } = pos.coords;
      const r = await fetch(`https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latitude}&lon=${longitude}&addressdetails=1&zoom=10`, {
        headers: { "Accept": "application/json" },
      });
      const data = await r.json();
      const addr = data.address || {};
      // Map ISO-2 country code → app country name expected by /api/meta
      const iso = (addr.country_code || "").toUpperCase();
      const codeMap = { GB: "United Kingdom", US: "United States", CA: "Canada", NG: "Nigeria", GH: "Ghana", KE: "Kenya", ZA: "South Africa", UG: "Uganda", TZ: "Tanzania", ET: "Ethiopia", EG: "Egypt", CM: "Cameroon", ZW: "Zimbabwe" };
      const country = codeMap[iso] || addr.country || form.country;
      // Pick the most specific locality available
      const city = addr.city || addr.town || addr.village || addr.suburb || addr.county || "";
      const updates = { lat: Number(latitude), lng: Number(longitude) };
      if (country) updates.country = country;
      if (city) {
        // If the resolved city is in the region list, pick it from the dropdown;
        // otherwise drop into custom input mode so the seller doesn't lose it.
        const regionsForCountry = meta.regions?.[country] || [];
        if (regionsForCountry.some((r) => r.toLowerCase() === city.toLowerCase())) {
          updates.city = regionsForCountry.find((r) => r.toLowerCase() === city.toLowerCase());
          setIsCustomCity(false);
        } else {
          updates.city = city;
          setIsCustomCity(true);
        }
      }
      // Auto-suggest local currency for the resolved country
      const suggested = resolveCcy(country);
      if (suggested && supported[suggested]) updates.currency = suggested;
      setForm((f) => ({ ...f, ...updates }));
      toast.success(`Located: ${[city, country].filter(Boolean).join(", ")}`);
    } catch (e) {
      const msg = e?.code === 1 ? "Location permission denied — please allow location in your browser" : "Could not get your location";
      toast.error(msg);
    } finally {
      setGeoLoading(false);
    }
  };

  const cats = form.type === "product" ? meta.product_categories : meta.service_categories;

  const generateDescription = async () => {
    if (!form.title.trim()) { toast.error("Enter a title first"); return; }
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/describe", {
        title: form.title,
        type: form.type,
        category: form.category,
        keywords: form.description,
      });
      update("description", data.description);
      toast.success("AI description ready");
    } catch (e) {
      toast.error("AI generation failed");
    } finally { setAiBusy(false); }
  };

  const onPickFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const uploaded = [];
      for (const f of Array.from(files).slice(0, 5)) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(`${BACKEND_URL}${data.url}`);
      }
      update("images", [...form.images, ...uploaded].slice(0, 5));
      toast.success(`${uploaded.length} image(s) uploaded`);
    } catch (e) {
      toast.error("Upload failed");
    } finally { setUploading(false); }
  };

  const removeImage = (i) => update("images", form.images.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.description || !form.category || !form.price) {
      toast.error("Please fill title, description, category and price"); return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form, price: Number(form.price) };
      // Services don't carry product variants
      if (payload.type === "service") {
        payload.sizes = []; payload.colors = []; payload.condition = null;
      }
      const { data } = await api.post("/listings", payload);
      toast.success("Listing published!");
      navigate(`/listing/${data.listing_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create listing");
    } finally { setSubmitting(false); }
  };

  if (!user) return null;

  // Gate: require admin-approved KYC before publishing
  if (user.verification_status !== "approved") {
    return (
      <div className="container-narrow py-16 max-w-2xl">
        <div className="label-overline text-[#C05A3C] mb-2">Almost there</div>
        <h1 className="font-display text-4xl mb-3">Get verified to publish listings</h1>
        <p className="text-muted-foreground mb-6 font-sans-ui">
          AfriMarket reviews every seller manually to keep our community safe. Submit your ID and
          proof of address — most reviews complete within 24–48 hours.
        </p>
        <div className="flex gap-3">
          <Button onClick={() => navigate("/verify")} className="bg-[#C05A3C] hover:bg-[#D46B4E] rounded-md" data-testid="goto-verify">
            Start verification
          </Button>
          <Button variant="ghost" onClick={() => navigate("/dashboard")}>Back to dashboard</Button>
        </div>
        {user.verification_status === "pending" && (
          <div className="mt-6 p-4 bg-[#D9923B]/10 border border-[#D9923B]/30 rounded-md text-sm">
            Your documents are <span className="font-semibold">under review</span>. We&apos;ll email you as soon as it&apos;s approved.
          </div>
        )}
        {user.verification_status === "rejected" && (
          <div className="mt-6 p-4 bg-[#B84A28]/10 border border-[#B84A28]/30 rounded-md text-sm">
            Your previous submission was rejected. Please resubmit from the verification page.
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="container-narrow py-10 max-w-3xl">
      <div className="label-overline text-[#C05A3C] mb-2">Create listing</div>
      <h1 className="font-display text-4xl mb-2">Tell the diaspora about it</h1>
      <p className="text-muted-foreground mb-8 font-sans-ui">Add a clear title, photos, price and let our AI polish the description.</p>

      <form onSubmit={submit} className="space-y-6 bg-white border border-black/10 rounded-lg p-6 sm:p-8" data-testid="create-listing-form">
        {/* Type tabs */}
        <div>
          <Label className="label-overline">I&apos;m offering</Label>
          <div className="mt-2 grid grid-cols-2 gap-2">
            {["product", "service"].map((t) => (
              <button
                key={t}
                type="button"
                data-testid={`type-${t}`}
                onClick={() => update("type", t)}
                className={`p-4 rounded-md border text-left transition-colors ${form.type === t ? "border-[#C05A3C] bg-[#C05A3C]/5" : "border-black/15 hover:border-black/30"}`}
              >
                <div className="font-display text-lg capitalize">{t}</div>
                <div className="text-xs text-muted-foreground">
                  {t === "product" ? "Physical items shipped from Africa" : "Local services you provide"}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Create with AI */}
        <div className="rounded-lg border border-[#C05A3C]/30 bg-[#C05A3C]/5 p-4" data-testid="ai-create-box">
          <Label className="label-overline flex items-center gap-1.5 text-[#C05A3C]">
            <Sparkles className="w-4 h-4" /> Create with AI
          </Label>
          <p className="text-xs text-muted-foreground mt-1 mb-2">
            Just type what you're selling — AI will suggest the title, category, price and description. You can edit everything after.
          </p>
          <div className="flex gap-2">
            <Input
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); runAiDraft(); } }}
              placeholder={form.type === "product" ? "e.g. handmade ankara tote bag" : "e.g. hair braiding at home in London"}
              data-testid="ai-create-prompt"
            />
            <Button type="button" onClick={runAiDraft} disabled={aiDrafting} data-testid="ai-create-btn" className="bg-[#C05A3C] hover:bg-[#D46B4E] text-white shrink-0">
              {aiDrafting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4 mr-1" /> Generate</>}
            </Button>
          </div>
        </div>

        {/* Title */}
        <div>
          <Label htmlFor="title" className="label-overline">Title</Label>
          <Input id="title" data-testid="input-title" value={form.title} onChange={(e) => update("title", e.target.value)} placeholder="e.g. Handwoven Ankara Tote Bag" className="mt-2"/>
        </div>

        {/* Category + country/city */}
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="label-overline">Category</Label>
            <NativeSelect data-testid="input-category" value={form.category} onValueChange={(v) => update("category", v)} className="mt-2" placeholder="Choose…">
              <option value="" disabled>Choose…</option>
              {cats?.map((c) => <option key={c} value={c}>{c}</option>)}
            </NativeSelect>
          </div>
          <div>
            <Label className="label-overline">Country</Label>
            <NativeSelect data-testid="input-country" value={form.country} onValueChange={(v) => {
              update("country", v); update("city", "");
              // Auto-suggest the country's local currency for new listings
              const suggested = resolveCcy(v);
              if (suggested && supported[suggested]) update("currency", suggested);
            }} className="mt-2">
              {meta.countries?.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
            </NativeSelect>
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-4">
          <div className="sm:col-span-1">
            <div className="flex items-baseline justify-between gap-2">
              <Label className="label-overline">Area / Region</Label>
              <button type="button" onClick={useMyLocation} disabled={geoLoading} className="text-[10px] uppercase tracking-wider font-semibold text-[#C05A3C] hover:underline disabled:opacity-50" data-testid="use-my-location-btn">
                {geoLoading ? "Locating…" : <><Navigation className="inline w-3 h-3 mr-0.5"/> Use my location</>}
              </button>
            </div>
            {meta.regions?.[form.country]?.length > 0 ? (
              <>
                <NativeSelect data-testid="input-city" value={isCustomCity ? "__other__" : (form.city || "")} onValueChange={(v) => {
                  if (v === "__other__") { setIsCustomCity(true); update("city", ""); }
                  else { setIsCustomCity(false); update("city", v); }
                }} className="mt-2" placeholder="Pick your area">
                  <option value="">Pick your area</option>
                  {meta.regions[form.country].map((r) => <option key={r} value={r}>{r}</option>)}
                  <option value="__other__">— Type my own city —</option>
                </NativeSelect>
                {isCustomCity && (
                  <Input
                    data-testid="input-city-custom"
                    value={form.city}
                    onChange={(e) => update("city", e.target.value)}
                    placeholder="Enter your town or city"
                    className="mt-2"
                    autoFocus
                  />
                )}
              </>
            ) : (
              <Input data-testid="input-city" value={form.city} onChange={(e) => update("city", e.target.value)} placeholder="Town or city" className="mt-2"/>
            )}
          </div>
          <div>
            <Label className="label-overline">{form.type === "service" ? "Rate" : "Price"}</Label>
            <div className="relative mt-2">
              <Input data-testid="input-price" type="number" min="0" step="0.01" value={form.price} onChange={(e) => update("price", e.target.value)} placeholder="0.00" className={form.type === "service" ? "pr-20" : ""}/>
              {form.type === "service" && rateBasisSuffix(form.rate_basis, "long") && (
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-muted-foreground pointer-events-none" data-testid="price-rate-suffix">{rateBasisSuffix(form.rate_basis, "long")}</span>
              )}
            </div>
            {/* Live take-home preview — updates as the seller types */}
            {(() => {
              const priceNum = parseFloat(form.price);
              if (!Number.isFinite(priceNum) || priceNum <= 0) return null;
              const isPro = !!user?.is_pro;
              const promoFree = !!meta.commission_free;
              const pct = promoFree ? 0 : (isPro ? 0.05 : (form.type === "service" ? 0.10 : 0.08));
              const fee = priceNum * pct;
              const takeHome = priceNum - fee;
              const sym = supported?.[form.currency]?.symbol?.trim() || form.currency;
              // Pro savings comparison: how much more they'd keep per sale + over 12 sales.
              const proFee = priceNum * 0.05;
              const proSavingsPerSale = !isPro ? Math.max(0, fee - proFee) : 0;
              const proSavingsAnnualish = proSavingsPerSale * 12;
              return (
                <div
                  className="mt-2 p-2.5 rounded-md bg-[#4A5D23]/8 border border-[#4A5D23]/20 text-xs space-y-1.5"
                  data-testid="create-listing-take-home"
                >
                  <div>
                    <span className="text-[#4A5D23] font-semibold">You&apos;ll receive ≈ </span>
                    <span className="font-display text-sm text-[#1A2024]" data-testid="create-listing-take-home-amount">{sym}{takeHome.toFixed(2)}</span>
                    <span className="text-muted-foreground"> per sale · {promoFree ? "0% fee 🎉" : `AfriMarket ${(pct * 100).toFixed(0)}% fee`}</span>
                  </div>
                  {promoFree && (
                    <div className="flex items-start gap-2 p-2 rounded bg-[#D9923B]/10 border border-[#D9923B]/30" data-testid="create-listing-promo-note">
                      <span className="text-base leading-none mt-0.5" aria-hidden="true">🎉</span>
                      <span className="flex-1 text-muted-foreground">
                        <strong className="text-[#1A2024]">Launch offer:</strong> 0% commission — you keep <strong className="text-[#1A2024]">100%</strong> of every sale for a limited time.
                      </span>
                    </div>
                  )}
                  {!promoFree && !isPro && proSavingsPerSale > 0 && (
                    <button
                      type="button"
                      onClick={() => navigate("/pro")}
                      className="w-full text-left flex items-start gap-2 p-2 rounded bg-[#D9923B]/10 border border-[#D9923B]/30 hover:bg-[#D9923B]/15 transition-colors"
                      data-testid="create-listing-take-home-pro-link"
                    >
                      <span className="text-base leading-none mt-0.5" aria-hidden="true">💸</span>
                      <span className="flex-1">
                        <span className="font-semibold text-[#1A2024]">Switch to Pro:</span>
                        <span className="text-muted-foreground"> keep <strong className="text-[#1A2024]">{sym}{proSavingsPerSale.toFixed(2)}</strong> more on this sale, ~<strong className="text-[#1A2024]">{sym}{proSavingsAnnualish.toFixed(2)}</strong> over 12 sales →</span>
                      </span>
                    </button>
                  )}
                </div>
              );
            })()}
            {form.type === "service" && (
              <NativeSelect data-testid="input-rate-basis" value={form.rate_basis || "hour"} onValueChange={(v) => update("rate_basis", v)} className="mt-2">
                {RATE_BASIS_OPTIONS.map((o) => <option key={o.value} value={o.value}>Charged {o.label}</option>)}
              </NativeSelect>
            )}
            {form.type === "service" && (
              <div className="text-[10px] text-muted-foreground mt-1">Pick how you charge — per hour for ongoing work, per session for one-off bookings, fixed for set-price gigs.</div>
            )}
          </div>
          <div>
            <Label className="label-overline">Currency</Label>
            <NativeSelect data-testid="input-currency" value={form.currency} onValueChange={(v) => update("currency", v)} className="mt-2">
              {Object.entries(supported).map(([code, meta]) => (
                <option key={code} value={code}>{code} — {meta.symbol.trim()} {meta.name}</option>
              ))}
            </NativeSelect>
          </div>
        </div>

        {/* Product variants — sizes, colours, condition. Hidden for services. */}
        {form.type === "product" && (
          <div className="border border-black/10 rounded-md p-4 bg-[#F3EFE9]/40">
            <div className="label-overline text-muted-foreground mb-3">Variants <span className="font-normal normal-case text-[10px]">(optional — helps buyers pick the right one)</span></div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold">Sizes available</Label>
                {(() => {
                  const presets = sizePresetsFor(form.category);
                  if (!presets) return null;
                  return (
                    <div className="mt-2 space-y-2" data-testid="size-presets">
                      {Object.entries(presets).map(([group, options]) => (
                        <div key={group}>
                          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">{group}</div>
                          <div className="flex flex-wrap gap-1">
                            {options.map((opt) => {
                              const active = form.sizes.includes(opt);
                              return (
                                <button
                                  type="button"
                                  key={opt}
                                  onClick={() => update("sizes", active ? form.sizes.filter((s) => s !== opt) : [...form.sizes, opt])}
                                  className={`text-[11px] font-semibold rounded px-2 h-7 transition-colors ${active ? "bg-[#1A2024] text-white border-[#1A2024]" : "bg-white border-black/15 text-[#1A2024] hover:border-[#1A2024]"} border`}
                                  data-testid={`size-preset-${opt.replace(/\s|"/g, "")}`}
                                >
                                  {opt}{active ? " ✓" : " +"}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
                <ChipInput
                  value={form.sizes}
                  onChange={(v) => update("sizes", v)}
                  placeholder="Or type your own — press Enter"
                  data-testid="input-sizes"
                  chipClass="bg-[#1A2024] text-white"
                  className="mt-2"
                />
                <div className="text-[10px] text-muted-foreground mt-1">Tap a preset above, or type free-form. Skip if not applicable.</div>
              </div>
              <div>
                <Label className="text-xs font-semibold">Colours available</Label>
                <div className="mt-2 flex flex-wrap gap-1" data-testid="color-presets">
                  {COLOR_PRESETS.map((opt) => {
                    const active = form.colors.includes(opt);
                    return (
                      <button
                        type="button"
                        key={opt}
                        onClick={() => update("colors", active ? form.colors.filter((c) => c !== opt) : [...form.colors, opt])}
                        className={`text-[11px] font-semibold rounded px-2 h-7 transition-colors ${active ? "bg-[#C05A3C] text-white border-[#C05A3C]" : "bg-white border-black/15 text-[#1A2024] hover:border-[#C05A3C]"} border`}
                        data-testid={`color-preset-${opt.replace(/\s/g, "")}`}
                      >
                        {opt}{active ? " ✓" : " +"}
                      </button>
                    );
                  })}
                </div>
                <ChipInput
                  value={form.colors}
                  onChange={(v) => update("colors", v)}
                  placeholder="Or type your own — press Enter"
                  data-testid="input-colors"
                  chipClass="bg-[#C05A3C] text-white"
                  className="mt-2"
                />
                <div className="text-[10px] text-muted-foreground mt-1">Tap a preset, or type. List every colour you stock.</div>
              </div>
            </div>
            <div className="mt-4">
              <Label className="text-xs font-semibold">Condition</Label>
              <NativeSelect data-testid="input-condition" value={form.condition || "new"} onValueChange={(v) => update("condition", v)} className="mt-1 max-w-xs">
                <option value="new">Brand new (sealed/unopened)</option>
                <option value="like_new">Like new (used once or twice)</option>
                <option value="used">Used (good condition, signs of wear)</option>
                <option value="refurbished">Refurbished / restored</option>
              </NativeSelect>
            </div>
          </div>
        )}

        {/* Pro-only: pin exact location */}
        {user.is_pro ? (
          <div className="border border-[#D9923B]/30 rounded-md p-4 bg-[#D9923B]/5">
            <div className="flex items-center gap-2 mb-1">
              <Crown className="w-4 h-4 text-[#D9923B]"/>
              <Label className="label-overline text-[#1A2024]">Pro perk · Pin exact location</Label>
            </div>
            <p className="text-xs text-muted-foreground mb-3">
              Drop a marker on the map so buyers see exactly where you are — perfect for local services and pickups.
              Optional; without a pin we use your country/area.
            </p>
            <LocationPicker
              value={{ lat: form.lat, lng: form.lng }}
              onChange={({ lat, lng }) => setForm((f) => ({ ...f, lat, lng }))}
              country={form.country}
              city={form.city}
            />
          </div>
        ) : (
          <div className="border border-dashed border-black/15 rounded-md p-4 flex items-start gap-3 bg-[#F3EFE9]/50">
            <MapPin className="w-5 h-5 text-[#1A2024] mt-0.5"/>
            <div className="flex-1">
              <div className="font-semibold text-sm flex items-center gap-1.5">
                Pin yourself on the map
                <Crown className="w-3.5 h-3.5 text-[#D9923B]"/>
                <span className="text-[10px] uppercase tracking-wider text-[#D9923B] font-bold">Pro</span>
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                Pro sellers can drop an exact pin (great for local services). Currently your listing maps to {form.country}{form.city ? `, ${form.city}` : ""}.
              </div>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={() => navigate("/pro")} className="border-[#D9923B] text-[#D9923B] hover:bg-[#D9923B]/10" data-testid="pin-upsell-btn">Upgrade</Button>
          </div>
        )}

        {/* Description with AI */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <Label htmlFor="desc" className="label-overline">Description</Label>
            <Button type="button" variant="ghost" size="sm" onClick={generateDescription} disabled={aiBusy} data-testid="ai-describe-btn" className="text-[#C05A3C] hover:bg-[#C05A3C]/10">
              {aiBusy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin"/> : <Sparkles className="w-3.5 h-3.5 mr-1"/>}
              Write with AI
            </Button>
          </div>
          <Textarea id="desc" data-testid="input-description" value={form.description} onChange={(e) => update("description", e.target.value)} placeholder="Describe your product or service. Click 'Write with AI' to generate." rows={7}/>
        </div>

        {/* Images */}
        <div>
          <Label className="label-overline">Photos (up to 5)</Label>
          <div className="mt-2 grid grid-cols-5 gap-2">
            {form.images.map((src, i) => (
              <div key={i} className="relative aspect-square rounded-md overflow-hidden border border-black/10">
                <img src={src} alt="" className="w-full h-full object-cover"/>
                <button type="button" onClick={() => removeImage(i)} className="absolute top-1 right-1 bg-white/90 rounded-full p-1 hover:bg-white" data-testid={`remove-image-${i}`}>
                  <X className="w-3 h-3"/>
                </button>
              </div>
            ))}
            {form.images.length < 5 && (
              <button type="button" onClick={() => fileRef.current?.click()} className="aspect-square rounded-md border-2 border-dashed border-black/20 flex flex-col items-center justify-center text-muted-foreground hover:border-[#C05A3C] hover:text-[#C05A3C]" data-testid="upload-image-btn">
                {uploading ? <Loader2 className="w-5 h-5 animate-spin"/> : <Upload className="w-5 h-5"/>}
                <span className="text-xs mt-1">Add</span>
              </button>
            )}
            <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={(e) => onPickFiles(e.target.files)}/>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-black/5">
          <Button type="button" variant="ghost" onClick={() => navigate(-1)} data-testid="cancel-create">Cancel</Button>
          <Button type="submit" disabled={submitting} className="bg-[#C05A3C] hover:bg-[#D46B4E] text-white rounded-md" data-testid="submit-listing">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2"/> : null}
            Publish listing
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CreateListing;
